import { useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash/debounce';

import type { Banner } from '../../../services/ui';
import { useGamesSeen } from '../../GamesList/hooks/useGamesSeen';
import { DEBOUNCE_DELAY, isElementSeen } from '../../GamesList/seenGamesImpression';

import { sendMainBannerImpression } from './rive/mainBannerAnalytics';

/**
 * What the player sees as "the main banner": the coin's admin banner (both its plain and its Rive background) and
 * the animation over it. A refetch of the same assets keeps the key; a coin switch onto different assets changes it.
 */
export const mainBannerAppearanceKey = (banner: Banner, animationOnly: Banner | null | undefined) =>
  JSON.stringify([
    banner.desktop,
    banner.mobile ?? null,
    banner.desktopBanner?.animationUrl ?? null,
    banner.desktopBanner?.animationBackgroundUrl ?? null,
    banner.mobileBanner?.animationUrl ?? null,
    banner.mobileBanner?.mobileAnimationBackgroundUrl ?? null,
    animationOnly?.desktopBanner?.animationUrl ?? null,
    animationOnly?.mobileBanner?.animationUrl ?? null,
  ]);

/**
 * Sends main_banner_impression once per appearance: once per mount, and again when `appearanceKey` changes to
 * another banner. Same "seen" check and cadence as the category banner (half the element on screen once scrolling
 * settles); scrolling it out and back within one appearance doesn't re-send. `null` = the main banner isn't shown.
 */
export function useMainBannerImpression(appearanceKey: string | null) {
  const bannerRef = useRef<HTMLImageElement>(null);
  const sentRef = useRef(false);
  const shownRef = useRef(appearanceKey !== null);
  shownRef.current = appearanceKey !== null;

  const seenListener = useMemo(
    () =>
      debounce(() => {
        const banner = bannerRef.current;
        if (sentRef.current || !shownRef.current || !banner || !isElementSeen(banner)) return;
        sentRef.current = true;
        void sendMainBannerImpression();
      }, DEBOUNCE_DELAY),
    [],
  );
  useEffect(() => () => seenListener.cancel(), [seenListener]);
  useGamesSeen(seenListener);

  // A new banner (or the banner back after the ToC one) is a new appearance: re-arm and check it without a scroll.
  useEffect(() => {
    sentRef.current = false;
    if (appearanceKey !== null) seenListener();
  }, [appearanceKey, seenListener]);

  return bannerRef;
}
